import React, { useEffect, useRef, useState } from 'react';
import './App.css';

function App() {
  const videoRef = useRef(null);
  const videoSectionRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    // Create floating particles
    const createParticle = () => {
      const particle = document.createElement('div');
      particle.className = 'floating-particle';
      particle.style.left = Math.random() * 100 + '%';
      particle.style.animationDuration = (Math.random() * 3 + 2) + 's';
      particle.style.animationDelay = Math.random() * 2 + 's';
      document.body.appendChild(particle);

      setTimeout(() => {
        particle.remove();
      }, 5000);
    };

    // Create particles periodically
    const interval = setInterval(createParticle, 2000);

    return () => clearInterval(interval);
  }, []);

  // Pause video when it leaves viewport
  useEffect(() => {
    const sectionEl = videoSectionRef.current;
    const videoEl = videoRef.current;
    if (!sectionEl || !videoEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && isPlaying) {
            // Pause when out of view
            videoEl.pause();
            setIsPlaying(false);
          }
        });
      },
      { threshold: 0 }
    );

    observer.observe(sectionEl);
    return () => {
      observer.disconnect();
    };
  }, [isPlaying]);

  const handlePlayClick = async () => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    try {
      if (!isPlaying) {
        // Try to play with sound
        videoEl.muted = false;
        setMuted(false);
        await videoEl.play();
        setIsPlaying(true);
      } else {
        videoEl.pause();
        setIsPlaying(false);
      }
    } catch (e) {
      // If unmuted play is blocked, try muted
      try {
        videoEl.muted = true;
        setMuted(true);
        await videoEl.play();
        setIsPlaying(true);
      } catch (err) {
        console.error('Video play failed:', err);
      }
    }
  };

  // Prewarm: start loading video in background when approaching viewport
  useEffect(() => {
    const sectionEl = videoSectionRef.current;
    const videoEl = videoRef.current;
    if (!sectionEl || !videoEl) return;

    const prewarmObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Start preloading video in background
            videoEl.preload = 'auto';
            videoEl.load();
          }
        });
      },
      { root: null, rootMargin: '500px 0px', threshold: 0 }
    );

    prewarmObserver.observe(sectionEl);
    return () => prewarmObserver.disconnect();
  }, []);

  return (
    <div className="App">
      <div className="scroll-indicator"></div>
      <div className="container">
        {/* FOMO Logo */}
        <div className="logo-container">
          <img src="/logo.png" alt="FOMO Logo" className="fomo-logo" />
        </div>

        {/* Upcoming Events */}
        <div className="events-section">
          <div className="events-header">
            <h2 className="events-title">Upcoming Events</h2>
            <div className="event-divider"></div>
          </div>

          <div className="event-card">
            <div className="event-info">
              <h3 className="event-name">FOMOween</h3>
              <p className="event-date">1st November</p>
            </div>

            <div className="flyer-container">
              <img src="/flyer.jpeg" alt="FOMOween Event Flyer" className="event-flyer" />
            </div>

            <div className="action-buttons">
              <a
                href="https://wa.me/917715800451?text=Hi%20I%20want%20to%20book%20tickets%20for%20FOMOween"
                target="_blank"
                rel="noopener noreferrer"
                className="whatsapp-section"
                aria-label="Book tickets via WhatsApp"
              >
                <div className="whatsapp-icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="white"
                    aria-hidden="true"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                </div>
                <p className="whatsapp-text">Book tickets via WhatsApp</p>
              </a>

              <a
                href="https://www.instagram.com/fomoofficiall?igsh=MTRxa25oN3N4eWxoYw=="
                target="_blank"
                rel="noopener noreferrer"
                className="instagram-section"
                aria-label="Follow FOMO on Instagram"
              >
                <div className="instagram-icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="white"
                    aria-hidden="true"
                  >
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.919-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <p className="instagram-text">Follow FOMO on Instagram</p>
              </a>
            </div>
          </div>

          {/* Promo Video Section */}
          <section className="promo-video-section" ref={videoSectionRef} aria-label="Co-Founder video invite">
            <div className="video-card">
              <div className="video-header">
                <h3 className="video-title">A Quick Invite</h3>
                <div className="video-divider" />
              </div>
              <div className="video-wrapper">
                <video
                  ref={videoRef}
                  className="promo-video"
                  src={`${process.env.PUBLIC_URL}/ad_video.MP4`}
                  loop
                  playsInline
                  muted={muted}
                  preload="auto"
                />
                {!isPlaying && (
                  <button
                    type="button"
                    className="play-button"
                    onClick={handlePlayClick}
                    aria-label="Play video"
                  >
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="11" fill="rgba(0,0,0,0.5)" stroke="#fff" strokeWidth="1"/>
                      <path d="M10 8l6 4-6 4V8z" fill="#fff"/>
                    </svg>
                  </button>
                )}
                {isPlaying && (
                  <button
                    type="button"
                    className={`mute-toggle ${!muted ? 'unmuted' : 'muted'}`}
                    aria-label={muted ? 'Unmute video' : 'Mute video'}
                    aria-pressed={!muted}
                    title={muted ? 'Unmute' : 'Mute'}
                    onClick={async () => {
                      const videoEl = videoRef.current;
                      if (!videoEl) return;
                      try {
                        if (muted) {
                          videoEl.muted = false;
                          setMuted(false);
                        } else {
                          videoEl.muted = true;
                          setMuted(true);
                        }
                      } catch (e) {
                        console.error('Mute toggle failed:', e);
                      }
                    }}
                  >
                    {muted ? (
                      // Muted: speaker with X
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M3 10v4h4l5 5V5L7 10H3z" fill="#fff"/>
                        <path d="M22 7l-5 5m0 0l5 5" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    ) : (
                      // Unmuted: speaker with waves
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M3 10v4h4l5 5V5L7 10H3z" fill="#fff"/>
                        <path d="M16 7.82a4 4 0 010 8.36M19 5a8 8 0 010 14" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    )}
                    <span className="mute-label">{muted ? 'Muted' : 'Sound On'}</span>
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* Party Info Section */}
          <div className="party-info-section">
            {/* Select Your Tickets */}
            <div className="tickets-container">
              <h2 className="tickets-title">Event Details</h2>
              <div className="title-underline"></div>

              {/* Event Details Card */}
              <div className="event-details-card">
                <div className="detail-row">
                  <span className="detail-emoji">📅</span>
                  <div className="detail-content">
                    <p className="detail-label">Date</p>
                    <p className="detail-value">November 1, 2025</p>
                  </div>
                </div>

                <div className="detail-divider"></div>

                <div className="detail-row">
                  <span className="detail-emoji">🕰️</span>
                  <div className="detail-content">
                    <p className="detail-label">Time</p>
                    <p className="detail-value">9:00 PM onwards</p>
                  </div>
                </div>

                <div className="detail-divider"></div>

                <div className="detail-row">
                  <span className="detail-emoji">📍</span>
                  <div className="detail-content">
                    <p className="detail-label">Venue</p>
                    <p className="detail-value">Madh, Mumbai</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Your Night Includes */}
            <div className="includes-container">
              <h2 className="includes-title">Your Night Includes</h2>
              <div className="title-underline"></div>

              <div className="includes-content">
                <div className="includes-left">
                  <div className="include-item">
                    <span className="include-bullet">•</span>
                    <p className="include-text">Access to Mumbai's most exclusive Halloween house party</p>
                  </div>

                  <div className="include-item">
                    <span className="include-bullet">•</span>
                    <p className="include-text">DJ, dance floor & themed zones</p>
                  </div>
                </div>

                <div className="includes-right">
                  <div className="include-item">
                    <span className="include-bullet">•</span>
                    <p className="include-text">Immersive Halloween décor</p>
                  </div>

                  <div className="include-item">
                    <span className="include-bullet">•</span>
                    <p className="include-text">Spooky-chic photo booth moments</p>
                  </div>
                </div>
              </div>

              <p className="spooktacular-text">Get ready for the most <span className="highlight-text">SPOOKTACULAR</span> night of the year!</p>
            </div>
          </div>
        </div>
        {/* Footer */}
        <footer className="site-footer" aria-label="Site footer">
          <small>
            powered by{' '}
            <a
              href="https://elyspr.com"
              target="_blank"
              rel="noopener noreferrer"
              className="brand-elyspr"
              aria-label="Elyspr (opens in a new tab)"
            >
              Elyspr
            </a>
          </small>
        </footer>
      </div>
    </div>
  );
}

export default App;
